import {
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableHeaderCellSortable,
  TableRow,
} from '@d-edge/overnight-hotelier-react'
import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { filterRecords, togglePreset, type SearchRecord } from '@/lib/filterExpression'
import { useDebouncedValue } from '@/lib/hooks'
import { Button, IconButton } from '../ui/Button'
import { EmptyState, ErrorState } from '../ui/EmptyState'
import { Icons } from '../ui/Icons'
import { LoadingBlock } from '../ui/Spinner'
import styles from './DataTable.module.css'

type Primitive = string | number | boolean | null | undefined

export interface Column<T> {
  key: string
  header: ReactNode
  /** Tooltip on the header; defaults to the key so users know the name to filter on. */
  title?: string
  width?: string
  align?: 'left' | 'center' | 'right'
  render?: (row: T) => ReactNode
  /** Primitive used for sorting and searching when the cell renders something richer. */
  value?: (row: T) => Primitive
  sortable?: boolean
  /** Searchable and sortable but never rendered (derived values such as a trend). */
  hidden?: boolean
  /** Hides the column when the table itself is narrower than ~560 (sm), ~760 (md) or ~1000 (lg) pixels. */
  hideBelow?: 'sm' | 'md' | 'lg'
}

export interface FilterPreset {
  label: string
  expression: string
  title?: string
}

interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (row: T) => string | number
  loading?: boolean
  error?: unknown
  searchable?: boolean
  /** Hint inside the search box; say what the rows are, e.g. "Search a hotel by name or id". */
  searchPlaceholder?: string
  presets?: FilterPreset[]
  toolbar?: ReactNode
  onRowClick?: (row: T) => void
  expandable?: (row: T) => ReactNode
  emptyMessage?: ReactNode
  dense?: boolean
  maxHeight?: string
  /** Shows a copy button that puts the filtered rows on the clipboard. */
  copyText?: (rows: T[]) => string
  initialSort?: { key: string; direction: 'asc' | 'desc' }
  footer?: ReactNode
}

function cellValue<T>(row: T, column: Column<T>): Primitive {
  if (column.value) return column.value(row)
  const raw = (row as Record<string, unknown>)[column.key]
  return typeof raw === 'object' && raw !== null ? JSON.stringify(raw) : (raw as Primitive)
}

/** Explains the filter language with this table's own column names, so the examples always apply. */
function searchHelp<T>(columns: Column<T>[], rows: T[] | undefined): string {
  const first = rows?.[0]
  const numeric = first === undefined ? undefined : columns.find((c) => !c.hidden && typeof cellValue(first, c) === 'number')
  const lines = ['Type text to keep the rows that contain it.', 'Several words must all match; start a word with ! to exclude it.']
  if (numeric) lines.push(`Compare a column: ${numeric.key}>10 (also <, >=, <=, =, !=).`)
  lines.push('Hover a column header to see the name to use.')
  return lines.join('\n')
}

/** The kit takes column widths as pixel numbers. */
function cellWidth(width?: string): number | undefined {
  const px = width ? Number.parseInt(width, 10) : Number.NaN
  return Number.isNaN(px) ? undefined : px
}

const hideClasses = { sm: styles.hideSm, md: styles.hideMd, lg: styles.hideLg }

/** Alignment plus the responsive hide class, shared by the header and body cells of a column. */
function cellClass<T>(column: Column<T>): string {
  return [column.align === 'center' ? styles.center : '', column.hideBelow ? hideClasses[column.hideBelow] : ''].filter(Boolean).join(' ')
}

/**
 * Generic table with the cockpit's filter language, column sorting, optional expandable rows
 * and quick-filter presets. Rows are plain objects; columns describe how to show them.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  searchable,
  searchPlaceholder,
  presets,
  toolbar,
  onRowClick,
  expandable,
  emptyMessage,
  dense,
  maxHeight,
  copyText,
  initialSort,
  footer,
}: DataTableProps<T>) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 200)
  const [sort, setSort] = useState(initialSort ?? null)
  const [expanded, setExpanded] = useState<Set<string | number>>(new Set())

  const toRecord = (row: T): SearchRecord => Object.fromEntries(columns.map((c) => [c.key, cellValue(row, c)]))

  const visible = useMemo(() => {
    if (!rows) return []
    const filtered = searchable ? filterRecords(rows, debouncedSearch, toRecord) : rows
    if (!sort) return filtered
    const column = columns.find((c) => c.key === sort.key)
    if (!column) return filtered
    const direction = sort.direction === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => compare(cellValue(a, column), cellValue(b, column)) * direction)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, debouncedSearch, sort, columns, searchable])

  const toggleSort = (column: Column<T>) => {
    if (column.sortable === false) return
    setSort((current) =>
      current?.key === column.key ? (current.direction === 'asc' ? { key: column.key, direction: 'desc' } : null) : { key: column.key, direction: 'asc' },
    )
  }

  const toggleExpanded = (key: string | number) =>
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const showToolbar = searchable || presets?.length || toolbar || copyText
  const visibleColumns = columns.filter((c) => !c.hidden)
  const columnCount = visibleColumns.length + (expandable ? 1 : 0)

  return (
    <div className={styles.wrapper}>
      {showToolbar && (
        <div className={styles.toolbar}>
          {searchable && (
            <div className={styles.search} title={searchHelp(columns, rows)}>
              <SearchInput icon fullWidth value={search} onChange={setSearch} placeholder={searchPlaceholder ?? 'Search'} clearButtonLabel="Clear search" aria-label="Filter rows" />
            </div>
          )}
          {presets && presets.length > 0 && (
            <div className={styles.presets}>
              {presets.map((preset) => (
                <Button key={preset.expression} size="sm" title={preset.title} active={search.includes(preset.expression)} onClick={() => setSearch(togglePreset(search, preset.expression))}>
                  {preset.label}
                </Button>
              ))}
            </div>
          )}
          {toolbar}
          <div className={styles.meta}>
            {rows && <span className={styles.count}>{visible.length === rows.length ? `${rows.length} rows` : `${visible.length} of ${rows.length}`}</span>}
            {copyText && rows && (
              <IconButton label="Copy filtered rows" onClick={() => void navigator.clipboard.writeText(copyText(visible))}>
                <Icons.Copy size={15} />
              </IconButton>
            )}
          </div>
        </div>
      )}

      <div className={styles.scroll} style={{ maxHeight }}>
        {error ? (
          <ErrorState error={error} />
        ) : loading || !rows ? (
          <LoadingBlock kind="table" />
        ) : visible.length === 0 ? (
          <EmptyState>{emptyMessage ?? (rows.length === 0 ? 'List is empty' : 'No rows match the filter')}</EmptyState>
        ) : (
          <Table className={`${styles.table} ${dense ? styles.dense : ''}`}>
            <TableHead className={styles.head}>
              <TableRow>
                {expandable && <TableHeaderCell className={styles.expandCell} />}
                {visibleColumns.map((column) =>
                  column.sortable === false ? (
                    <TableHeaderCell key={column.key} title={column.title ?? column.key} cellWidth={cellWidth(column.width)} textAlignEnd={column.align === 'right'} className={cellClass(column)}>
                      {column.header}
                    </TableHeaderCell>
                  ) : (
                    <TableHeaderCellSortable
                      key={column.key}
                      title={column.title ?? column.key}
                      cellWidth={cellWidth(column.width)}
                      textAlignEnd={column.align === 'right'}
                      className={cellClass(column)}
                      sort={sort?.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                      onClick={() => toggleSort(column)}
                    >
                      {column.header}
                    </TableHeaderCellSortable>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((row) => {
                const key = rowKey(row)
                const isExpanded = expanded.has(key)
                const rowProps = onRowClick ? { onRowClick: () => onRowClick(row), rowTriggerLabel: `Open ${key}` } : {}
                return (
                  <Fragment key={key}>
                    <TableRow className={isExpanded ? styles.expandedParent : ''} {...rowProps}>
                      {expandable && (
                        <TableCell className={styles.expandCell}>
                          <IconButton label={isExpanded ? 'Collapse' : 'Expand'} onClick={() => toggleExpanded(key)}>
                            {isExpanded ? <Icons.ChevronDown size={14} /> : <Icons.ChevronRight size={14} />}
                          </IconButton>
                        </TableCell>
                      )}
                      {visibleColumns.map((column) => (
                        <TableCell key={column.key} textAlignEnd={column.align === 'right'} className={`${styles.cell} ${cellClass(column)}`}>
                          {column.render ? column.render(row) : String(cellValue(row, column) ?? '')}
                        </TableCell>
                      ))}
                    </TableRow>
                    {expandable && isExpanded && (
                      <TableRow className={styles.expandedRow}>
                        <TableCell colSpan={columnCount}>{expandable(row)}</TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  )
}

function compare(a: Primitive, b: Primitive): number {
  if (a === b) return 0
  if (a === null || a === undefined) return 1
  if (b === null || b === undefined) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}
